import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { useI18n } from '../../contexts/I18nContext';
import { isAbortError } from '../../utils/abortError';
import { clientAccessService } from '../../services/clientAccessService';
import { prefetchClientUsers } from '../clients/clientUsersCache';
import { indexClients, isAgency, rootOf } from '../../utils/clientTree';
import { hueOf } from './UserAvatar';
import { clientInitials } from '../clients/clientInitials';

interface ClientsUserOverviewProps {
  onClientSelect: (client: Client) => void;
  /** Opens the account creation dialog on this client (an agency, or the client itself). */
  onCreateUser?: (client: Client) => void;
  refreshTrigger?: number;
  /**
   * Skip the picker when the caller belongs to exactly one client. A
   * ClientAdmin has a single organisation, so a one-row table is a click
   * that carries no decision.
   */
  autoSelectSingle?: boolean;
}

type SortField = 'name' | 'siren' | 'active' | 'users' | 'created';
type StatusFilter = 'all' | 'active' | 'inactive';

/**
 * Client-first entry point to user management: pick the organisation,
 * then work inside it. One row per client with how many accounts it has,
 * a search box, a status filter and sortable columns.
 */
export const ClientsUserOverview: React.FC<ClientsUserOverviewProps> = ({
  onClientSelect,
  onCreateUser,
  refreshTrigger,
  autoSelectSingle = false,
}) => {
  const { formatDate, formatNumber, t } = useI18n();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [userCounts, setUserCounts] = useState<Record<string, number> | null>(null);
  const [folded, setFolded] = useState<Set<string>>(new Set());

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // The API already scopes this to what the caller may see.
      const data = await clientService.fetchAllClients();
      setClients(data || []);
      // Counts arrive separately and never block the list.
      clientAccessService.fetchUserCounts().then(setUserCounts).catch(() => setUserCounts({}));
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[CLIENTS OVERVIEW] load failed', err);
      setError(t('clients.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients, refreshTrigger]);

  useEffect(() => {
    if (autoSelectSingle && !loading && clients.length === 1) {
      onClientSelect(clients[0]);
    }
    // onClientSelect identity changes on every parent render; tracking it
    // would re-fire the selection endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectSingle, loading, clients]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'users' || field === 'created' ? 'desc' : 'asc');
    }
  };

  const count = (c: Client) => userCounts?.[c.id] ?? 0;
  const byId = useMemo(() => indexClients(clients), [clients]);
  const totalUsers = useMemo(() => clients.reduce((s, c) => s + count(c), 0), [clients, userCounts]); // eslint-disable-line react-hooks/exhaustive-deps
  const inactiveCount = clients.filter((c) => !c.active).length;
  const agencyCount = clients.filter((c) => isAgency(c)).length;

  // Agencies sit under their root (the root stays in view when an agency
  // matches); an agency whose root is out of scope is a line of its own.
  const visible = useMemo<Array<Client & { depth: 0 | 1; agencyCount: number; groupAccounts: number }>>(() => {
    const needle = searchTerm.trim().toLowerCase();
    const statusOk = (c: Client) => !((status === 'active' && !c.active) || (status === 'inactive' && c.active));
    const textMatch = (c: Client) => !needle || (c.name || '').toLowerCase().includes(needle) || (c.siren || '').includes(needle);
    const dir = sortDirection === 'asc' ? 1 : -1;
    const compare = (a: Client, b: Client) => {
      if (sortField === 'active') return ((a.active ? 1 : 0) - (b.active ? 1 : 0)) * dir;
      if (sortField === 'users') return (count(a) - count(b)) * dir;
      if (sortField === 'created') return ((a.created_at || '').localeCompare(b.created_at || '')) * dir;
      const av = (sortField === 'siren' ? a.siren : a.name) || '';
      const bv = (sortField === 'siren' ? b.siren : b.name) || '';
      return av.localeCompare(bv) * dir;
    };
    const agencies = new Map<string, Client[]>();
    clients.forEach((c) => { if (c.parent_id && byId.has(c.parent_id)) agencies.set(c.parent_id, [...(agencies.get(c.parent_id) || []), c]); });
    const roots = clients.filter((c) => !(c.parent_id && byId.has(c.parent_id)));
    const rows: Array<Client & { depth: 0 | 1; agencyCount: number; groupAccounts: number }> = [];
    roots
      .filter((root) => statusOk(root) && (textMatch(root) || (agencies.get(root.id) || []).some((a) => statusOk(a) && textMatch(a))))
      .sort(compare)
      .forEach((root) => {
        const all = agencies.get(root.id) || [];
        rows.push({ ...root, depth: 0, agencyCount: all.length, groupAccounts: count(root) + all.reduce((s, a) => s + count(a), 0) });
        const shown = all.filter((a) => statusOk(a) && (textMatch(a) || textMatch(root))).sort((a, b) => a.name.localeCompare(b.name));
        // Folded by the chevron, unless the search names one of the agencies.
        if (!folded.has(root.id) || (needle && shown.some((a) => textMatch(a) && !textMatch(root)))) {
          shown.forEach((a) => rows.push({ ...a, depth: 1, agencyCount: 0, groupAccounts: count(a) }));
        }
      });
    return rows;
  }, [clients, byId, searchTerm, status, sortField, sortDirection, userCounts, folded]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleFold = (id: string) => setFolded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  const header = (field: SortField, label: string, numeric = false) => (
    <th className={numeric ? 'numeric' : undefined} aria-sort={sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="users-sort" onClick={() => handleSort(field)}>
        {label}<span className="users-sort-mark" aria-hidden="true">{sortField === field ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );

  if (loading) {
    return (
      <div className="users-card users-state" data-testid="clients-overview-loading">
        <div className="users-spinner" />
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="users-card users-state" data-testid="clients-overview-error" role="alert">
        <p>{error}</p>
        <button type="button" className="btn btn-primary btn-sm" onClick={fetchClients}>{t('errors.retryAction')}</button>
      </div>
    );
  }

  return (
    <div className="users-card" data-testid="clients-overview">
      <div className="users-toolbar">
        <input
          type="search"
          data-testid="clients-overview-search"
          className="users-search"
          placeholder={t('clients.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label={t('clients.searchPlaceholder')}
        />
        <select className="users-select" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)} aria-label={t('clients.status')} data-testid="clients-overview-status">
          <option value="all">{t('users.overview.statusAll')}</option>
          <option value="active">{t('users.overview.statusActive')}</option>
          <option value="inactive">{t('users.overview.statusSuspended')}</option>
        </select>
        <div className="users-toolbar-facts" data-testid="clients-overview-facts">
          <span><strong>{formatNumber(clients.length)}</strong> {t('users.overview.clients')}</span>
          <span><strong>{userCounts ? formatNumber(totalUsers) : '…'}</strong> {t('users.overview.accounts')}</span>
          {agencyCount > 0 && <span><strong>{formatNumber(agencyCount)}</strong> {t('users.overview.agencies')}</span>}
          {inactiveCount > 0 && <span className="is-warning"><strong>{formatNumber(inactiveCount)}</strong> {t('users.overview.suspended')}</span>}
        </div>
      </div>

      <div className="users-table-wrap">
        <table className="users-table-v2" data-testid="clients-overview-table">
          <thead>
            <tr>
              {header('name', t('clients.clientName'))}
              {header('users', t('users.overview.colUsers'), true)}
              {header('active', t('clients.status'))}
              {header('created', t('clients.created'))}
              <th className="users-col-open"><span className="sr-only">{t('users.overview.open')}</span></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="users-empty">{clients.length === 0 ? t('users.noClientAvailable') : t('users.overview.noMatch')}</td>
              </tr>
            ) : (
              visible.map((client) => {
                const own = count(client);
                const group = client.agencyCount > 0;
                const shown = group ? client.groupAccounts : own;
                return (
                  <tr
                    key={client.id}
                    className={`users-row${client.active ? '' : ' is-off'}${client.depth ? ' is-agency-row' : ''}${group ? ' is-group-row' : ''}`}
                    data-testid={`client-row-${client.id}`}
                    onClick={() => onClientSelect(client)}
                    onMouseEnter={() => prefetchClientUsers(client.id)}
                    onFocus={() => prefetchClientUsers(client.id)}
                  >
                    <td className={client.depth ? 'users-agency-cell' : undefined}>
                      <div className="users-client-line">
                        {client.depth === 1 && <span className="users-tree-branch" aria-hidden="true">└</span>}
                        <span className="users-client-mark" style={{ ['--cl-hue' as any]: hueOf(client.id) }} aria-hidden="true">{clientInitials(client.name)}</span>
                        <div className="users-client-text">
                          <button
                            type="button"
                            className="users-name-link"
                            onClick={(e) => { e.stopPropagation(); onClientSelect(client); }}
                          >
                            {client.name}
                            {isAgency(client) && <span className="cl-agency-badge">{t('clients.agencies.badge')}</span>}
                          </button>
                          <span className="users-sub mono">
                            {client.siren
                              ? `SIREN ${client.siren}`
                              : isAgency(client)
                                ? (client.nic ? `${t('clients.agencies.nic')} ${client.nic}` : t('clients.agencies.agencyOf', { name: rootOf(client, byId).name }))
                                : t('users.overview.noSiren')}
                          </span>
                        </div>
                        {group && (
                          <button
                            type="button"
                            className="users-tree-toggle"
                            data-testid={`toggle-agencies-${client.id}`}
                            aria-expanded={!folded.has(client.id)}
                            onClick={(e) => { e.stopPropagation(); toggleFold(client.id); }}
                          >
                            <span aria-hidden="true">{folded.has(client.id) ? '▸' : '▾'}</span> {t('clients.agencies.count', { count: client.agencyCount })}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="numeric users-count-cell">
                      <span className="users-count">{userCounts ? formatNumber(shown) : '…'}</span>
                      {group && userCounts && <span className="users-count-note">{t('clients.agencies.ofWhichHeadOffice', { count: formatNumber(own) })}</span>}
                    </td>
                    <td>
                      <span className={`status-badge ${client.active ? 'active' : 'inactive'}`}>
                        {client.active ? t('clients.active') : t('users.overview.statusSuspended')}
                      </span>
                    </td>
                    <td className="users-muted">{client.created_at ? formatDate(new Date(client.created_at)) : '–'}</td>
                    <td className="users-col-open users-col-actions">
                      {onCreateUser && client.active && (
                        <button
                          type="button"
                          className="users-row-action"
                          data-testid={`btn-add-user-${client.id}`}
                          title={t('users.overview.addAccountTo', { name: client.name })}
                          onClick={(e) => { e.stopPropagation(); onCreateUser(client); }}
                        >
                          + {t('users.overview.addAccount')}
                        </button>
                      )}
                      <span className="users-chevron" aria-hidden="true">›</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
